import { apiRequest } from '../../shared/api/client';

/**
 * Sales orders — mirrors Invetrak.Api.Endpoints.SalesOrderEndpoints.
 *
 *   GET    /api/v1/sales-orders                        orders:read
 *   GET    /api/v1/sales-orders/{id}                   orders:read
 *   POST   /api/v1/sales-orders                        orders:write
 *   PUT    /api/v1/sales-orders/{id}                   orders:write
 *   POST   /api/v1/sales-orders/{id}/status            orders:write  (+ orders:cancel to close)
 *   DELETE /api/v1/sales-orders/{id}                   orders:write
 *   GET    /api/v1/sales-orders/{id}/lines             orders:read
 *   PUT    /api/v1/sales-orders/{id}/lines             orders:write
 *   DELETE /api/v1/sales-orders/{id}/lines/{lineId}    orders:write
 *
 * Three things are never in a request body, because the server owns them:
 *   - the order number, allocated from the SALES_ORDER sequence at creation;
 *   - the warehouse, taken from the session rather than stated;
 *   - the totals and the shipped part of the status, derived from the lines.
 */

const COOKIE_INIT: RequestInit = { credentials: 'include' };

/**
 * Legacy's seven, and only three of them are anybody's decision.
 *
 * These are `tblWebOrderStatus` 1-7, settled in tenant script 061. `picked`, `shipped`,
 * `back_order` and `in_wave_pick` are observations the server makes — about the pick, the shipment
 * or the wave — so nothing here sets them. See the status endpoint, which takes an ACTION.
 *
 * There is no `draft` and no `cancelled`. Legacy creates an order at `open` and takes one out of
 * play by CLOSING it, which is why closing needs the cancel permission.
 */
export type SalesOrderStatus =
  | 'open'
  | 'released'
  | 'picked'
  | 'shipped'
  | 'back_order'
  | 'in_wave_pick'
  | 'closed';

/** The explicit acts. Everything else about an order's progress is derived. */
export type SalesOrderAction = 'release' | 'close' | 'reopen';

/**
 * Anything still owed to a customer — what the list opens on.
 *
 * Legacy writes `OrderStatus in (1,2,5)` in a dozen procedures. `back_order` belongs here and is
 * the one people forget: an order shipped short is still an order the customer is waiting on.
 */
export const OPEN_STATUSES: SalesOrderStatus[] = ['open', 'released', 'back_order'];

/** The statuses from which `release` is allowed. Legacy's OrderHdrController guard, 1 or 5. */
export const RELEASABLE_STATUSES: SalesOrderStatus[] = ['open', 'back_order'];

export interface SalesOrderDto {
  salesOrderId: string;
  orderNumber: string;
  warehouseId: string;
  warehouseCode: string;
  customerId: string;
  customerNumber: string;
  customerName: string;
  customerShipToId: string | null;
  shipToCode: string | null;
  shipToName: string | null;
  /** The customer's OWN purchase order number — what their accounts payable matches on. */
  customerPo: string | null;
  referenceNumber: string | null;
  /** Who at the CUSTOMER placed it. A person, not a user of this system. */
  orderedBy: string | null;
  /** Whether they will take a partial shipment. Decides what a short pick does, so it is a rule. */
  acceptPartial: boolean;
  orderTypeCode: string | null;
  salesRepId: string | null;
  shipMethodId: string | null;
  paymentTermId: string | null;
  paymentTermCode: string | null;
  orderDateUtc: string;
  requestedShipDateUtc: string | null;
  status: SalesOrderStatus;
  specialInstructions: string | null;
  subtotalAmount: number;
  taxAmount: number;
  freightAmount: number;
  totalAmount: number;
  currencyCode: string;
  closedAtUtc: string | null;
  closedByUserId: string | null;
  createdAtUtc: string;
  lastModifiedUtc: string;
  lineCount: number;
  /** Whether anything has left the building. Decides if it can be deleted or reopened. */
  hasShipments: boolean;
  hasAllocations: boolean;
}

export interface SalesOrderLineDto {
  salesOrderLineId: string;
  salesOrderId: string;
  lineNumber: number;
  itemId: string;
  itemNumber: string;
  itemDescription: string;
  quantityOrdered: number;
  quantityAllocated: number;
  quantityShipped: number;
  /** legacy's BalanceDue. Derived by the server so nobody subtracts by hand. */
  quantityOutstanding: number;
  uomId: string;
  uomCode: string;
  unitPrice: number;
  lineTotal: number;
  status: string;

  /**
   * What priced the line — tenant script 075.
   *
   * Null on lines saved before 075: those were priced at list with no discount, and the server
   * does not backfill because that would be inventing history.
   */
  listUnitPrice: number | null;
  /** A fraction: 0.10 is ten per cent. */
  discount: number;
  /** list, item_tier, customer_percentage, contract or override. */
  priceSource: string | null;
}

export interface NewSalesOrderInput {
  customerId: string;
  customerShipToId?: string | null;
  customerPo?: string | null;
  referenceNumber?: string | null;
  orderedBy?: string | null;
  acceptPartial?: boolean;
  requestedShipDateUtc?: string | null;
  paymentTermId?: string | null;
  specialInstructions?: string | null;
}

export interface SalesOrderEditInput {
  customerShipToId?: string | null;
  customerPo?: string | null;
  referenceNumber?: string | null;
  orderedBy?: string | null;
  acceptPartial?: boolean | null;
  requestedShipDateUtc?: string | null;
  paymentTermId?: string | null;
  specialInstructions?: string | null;
  freightAmount?: number | null;
  taxAmount?: number | null;
}

export interface SalesOrderLineInput {
  salesOrderLineId?: string | null;
  itemId: string;
  quantityOrdered: number;
  uomId?: string | null;
  /** Omitted takes the item's list price. Real pricing is M5.13–M5.15. */
  unitPrice?: number | null;
}

export async function listSalesOrders(
  options: { status?: SalesOrderStatus; openOnly?: boolean; customerId?: string } = {},
  signal?: AbortSignal,
): Promise<SalesOrderDto[]> {
  const params = new URLSearchParams();
  if (options.status) params.set('status', options.status);
  if (options.openOnly) params.set('openOnly', 'true');
  if (options.customerId) params.set('customerId', options.customerId);
  const query = params.toString();

  const { data } = await apiRequest<SalesOrderDto[]>(
    `/v1/sales-orders/${query ? `?${query}` : ''}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

export async function getSalesOrder(id: string, signal?: AbortSignal): Promise<SalesOrderDto> {
  const { data } = await apiRequest<SalesOrderDto>(`/v1/sales-orders/${encodeURIComponent(id)}`, {
    method: 'GET',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function createSalesOrder(body: NewSalesOrderInput): Promise<SalesOrderDto> {
  const { data } = await apiRequest<SalesOrderDto>('/v1/sales-orders/', {
    method: 'POST',
    body,
    ...COOKIE_INIT,
  });
  return data;
}

export async function updateSalesOrder(
  id: string,
  body: SalesOrderEditInput,
): Promise<SalesOrderDto> {
  const { data } = await apiRequest<SalesOrderDto>(`/v1/sales-orders/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body,
    ...COOKIE_INIT,
  });
  return data;
}

/**
 * release, close or reopen.
 *
 * An ACTION, not a status: only three transitions exist and each has guards, so a caller that
 * could name a status would be naming something the server may refuse for reasons the caller
 * cannot see.
 */
export async function setSalesOrderStatus(
  id: string,
  action: SalesOrderAction,
): Promise<SalesOrderDto> {
  const { data } = await apiRequest<SalesOrderDto>(
    `/v1/sales-orders/${encodeURIComponent(id)}/status`,
    { method: 'POST', body: { action }, ...COOKIE_INIT },
  );
  return data;
}

export async function deleteSalesOrder(id: string): Promise<void> {
  await apiRequest<void>(`/v1/sales-orders/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    ...COOKIE_INIT,
  });
}

export async function listSalesOrderLines(
  id: string,
  signal?: AbortSignal,
): Promise<SalesOrderLineDto[]> {
  const { data } = await apiRequest<SalesOrderLineDto[]>(
    `/v1/sales-orders/${encodeURIComponent(id)}/lines`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

/** Insert or update, decided by whether the input carries a line id. */
export async function saveSalesOrderLine(
  id: string,
  body: SalesOrderLineInput,
): Promise<SalesOrderLineDto> {
  const { data } = await apiRequest<SalesOrderLineDto>(
    `/v1/sales-orders/${encodeURIComponent(id)}/lines`,
    { method: 'PUT', body, ...COOKIE_INIT },
  );
  return data;
}

export async function deleteSalesOrderLine(id: string, lineId: string): Promise<void> {
  await apiRequest<void>(
    `/v1/sales-orders/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}`,
    { method: 'DELETE', ...COOKIE_INIT },
  );
}

/** Nothing further can happen to the order by itself. */
export function isFinalStatus(status: SalesOrderStatus): boolean {
  return status === 'closed';
}
